
import chargerActionTypes from '../types/chargerActionTypes';
import connectorActionTypes from '../types/connectorActionTypes';

const initialState = {
    chargers: {
        chargersArray: [],
        publicChargersArray: [],
        driverChargersArray: []
    },
    favoriteChargersIds: [],
    selectedCharger: null,
    myActiveChargersIds: [],
    chargingSummery: null,
    currentTransaction: null,
    // spec 000196 — live charging-session state per transaction id. Empty for every session on a
    // tariff that has not opted in, which is what makes those screens render exactly as they do now.
    chargingSessionStates: {},
    // spec 000220 — { type: 'start' | 'stop', connectorId, startedAt } while a start/stop tap waits for the live
    // status to confirm it; null otherwise. Drives the global loader and the connector screen's action message.
    pendingChargingAction: null,
    // spec 000220 (Part C) — connector id whose screen is inside the 000023 waiting window; the title band reads it.
    waitingForChargingConnectorId: null,
    stateInfo: {
        isLoading: false,
        isSuccess: false,
        isError: false,
        error: null,
        isInitialFetch: false
    }

};

const chargerReducer = (state = initialState, action) => {
    switch (action.type) {
        case chargerActionTypes.START_ADD_CHARGER:
        case chargerActionTypes.START_DELETE_CHARGER:
        case chargerActionTypes.START_UPDATE_CHARGER:
        case chargerActionTypes.START_GET_CHARGERS:
            return {
                ...state,
                stateInfo: {
                    ...state.stateInfo,
                    isLoading: true,
                    isSuccess: false,
                    isError: false,
                    error: null
                }
            };
        /*
         * spec 000220 — a start/stop tap no longer uses `stateInfo.isLoading`. Nothing ever cleared it on success:
         * START_TRANSACTION_SUCCESS had no case here, and the stop case was spelled `chargerActionTypes.
         * STOP_TRANSACTION_SUCCESS` — a constant that does not exist, i.e. `case undefined:`. Until spec 000209 (A4)
         * the next SignalR status frame overwrote the flag and hid that; since then the loader stuck for good.
         *
         * The tap now opens `pendingChargingAction`, which ends when the live status confirms the change, on
         * failure, on timeout or when the screen unmounts (useChargingActionPending). The HTTP success is
         * deliberately NOT an end: it races the status frame, and closing on it lets the loader disappear while
         * the banner still shows the old state.
         */
        case connectorActionTypes.START_STOP_TRANSACTION:
        case connectorActionTypes.START_START_TRANSACTION:
        case connectorActionTypes.START_START_SMART_TRANSACTION:
            return {
                ...state,
                pendingChargingAction: action.payload || null,
                stateInfo: {
                    ...state.stateInfo,
                    isSuccess: false,
                    isError: false,
                    error: null
                }
            };
        case connectorActionTypes.START_TRANSACTION_SUCCESS:
        case connectorActionTypes.START_SMART_TRANSACTION_SUCCESS:
        case connectorActionTypes.STOP_TRANSACTION_SUCCESS:
            return state;
        case connectorActionTypes.START_TRANSACTION_FAILURE:
        case connectorActionTypes.STOP_TRANSACTION_FAILURE:
        case connectorActionTypes.START_SMART_TRANSACTION_FAILURE:
            return {
                ...state,
                pendingChargingAction: null,
                stateInfo: {
                    ...state.stateInfo,
                    isLoading: false,
                    isSuccess: false,
                    isError: true,
                    error: action.payload,
                }
            };
        case connectorActionTypes.CLEAR_PENDING_CHARGING_ACTION:
            if (!state.pendingChargingAction || state.pendingChargingAction.connectorId !== action.payload?.connectorId) {
                return state;
            }
            return {
                ...state,
                pendingChargingAction: null,
            };
        case connectorActionTypes.SET_WAITING_FOR_CHARGING: {
            const { connectorId, isWaiting } = action.payload || {};
            if (isWaiting) {
                return state.waitingForChargingConnectorId === connectorId ? state : { ...state, waitingForChargingConnectorId: connectorId };
            }
            // Only the connector that raised it may lower it, so a screen that is leaving cannot clear the next one.
            return state.waitingForChargingConnectorId === connectorId ? { ...state, waitingForChargingConnectorId: null } : state;
        }
        case chargerActionTypes.ADD_CHARGER_SUCCESS:
        case chargerActionTypes.UPDATE_CHARGER_SUCCESS:
        case chargerActionTypes.DELETE_CHARGER_SUCCESS:
        case chargerActionTypes.REMOVE_FAVORITE_CHARGER_SUCCESS:
        case chargerActionTypes.ADD_FAVORITE_CHARGER_SUCCESS:
            return {
                ...state,
                stateInfo: {
                    ...state.stateInfo,
                    isLoading: false,
                    isSuccess: true,
                    isError: false
                }
            };
        case chargerActionTypes.ADD_CHARGER_FAILURE:
        case chargerActionTypes.UPDATE_CHARGER_FAILURE:
        case chargerActionTypes.DELETE_CHARGER_FAILURE:
        case chargerActionTypes.ADD_FAVORITE_CHARGER_FAILURE:
        case chargerActionTypes.REMOVE_FAVORITE_CHARGER_FAILURE:
            return {
                ...state,
                stateInfo: {
                    ...state.stateInfo,
                    isLoading: false,
                    isSuccess: false,
                    isError: true,
                    error: action.payload,
                }
            };


        case chargerActionTypes.GET_ALL_CHARGERS_SUCCESS:
            // Check if all the arrays in the chargers object are empty
            const hasNoChargersYet = Object.values(state.chargers).every(array => array.length === 0);
            return {
                ...state,
                chargers: action.payload,
                stateInfo: {
                    ...state.stateInfo,
                    // Update isInitialFetch based on whether any chargers exist
                    isInitialFetch: hasNoChargersYet
                }
            };
        case chargerActionTypes.GET_CHARGER_SUCCESS:
            return {
                ...state,
                chargers: {
                    ...state.chargers,
                    chargersArray: action.payload
                },
                stateInfo: {
                    ...state.stateInfo,
                    // Update isInitialFetch based on whether any chargers exist
                    isInitialFetch: false
                }
            };
        case chargerActionTypes.GET_DRIVER_CHARGER_SUCCESS:
            return {
                ...state,
                chargers: {
                    ...state.chargers,
                    driverChargersArray: action.payload
                }
            };
        case chargerActionTypes.GET_PUBLIC_CHARGER_SUCCESS:
            return {
                ...state,
                chargers: {
                    ...state.chargers,
                    publicChargersArray: action.payload
                },
                stateInfo: {
                    ...state.stateInfo,
                    isLoading: false
                }
            };
        case chargerActionTypes.GET_FAVORITE_SUCCESS:
            return {
                ...state,
                favoriteChargersIds: action.payload,
                stateInfo: {
                    ...state.stateInfo,
                    isLoading: false
                }
            };
        case chargerActionTypes.SELECT_CHARGER:
            return {
                ...state,
                selectedCharger: action.payload
            };
        case chargerActionTypes.SET_MY_ACTIVE_CHARGER_ID:
            return {
                ...state,
                myActiveChargersIds: action.payload
            };
        case chargerActionTypes.UNSELECT_CHARGER:
            return {
                ...state,
                selectedCharger: initialState.selectedCharger
            };
        case chargerActionTypes.RESET:
            return {
                ...initialState
            };

        //signalR
        case chargerActionTypes.UPDATE_TRANSACTION_DATA:
            return {
                ...state,
                chargers: {
                    ...state.chargers,
                    chargersArray: updateTransactionData(state.chargers.chargersArray, action.payload),
                    driverChargersArray: updateTransactionData(state.chargers.driverChargersArray, action.payload),
                    publicChargersArray: updateTransactionData(state.chargers.publicChargersArray, action.payload),
                }
            };
        // spec 000196 — the live session state, kept in a map keyed by transaction id.
        //
        // A frame with no transaction id is dropped rather than stored under a made-up key: it
        // carries money, and money attached to the wrong session is worse than money not shown.
        case chargerActionTypes.UPDATE_CHARGING_SESSION_STATE: {
            const sessionState = action.payload;
            if (!sessionState || !sessionState.transactionId) return state;

            return {
                ...state,
                chargingSessionStates: {
                    ...state.chargingSessionStates,
                    // The whole frame replaces the previous one rather than merging into it. Merging
                    // would let a stale field survive a state change — a countdown, say, outliving
                    // the grace period it belonged to.
                    //
                    // spec 000196 Round 5 (R5.B4) — stamped with the moment it arrived, so the screen can
                    // extend the accrued amount at the frozen rate until the next frame.
                    [sessionState.transactionId]: { ...sessionState, receivedAt: Date.now() },
                },
            };
        }

        // spec 000273 — the state read from the server for a screen opened after the live frame. A live frame that
        // arrived after the request started is newer than this answer, so it wins; otherwise the answer is stored
        // exactly as a live frame would be.
        case chargerActionTypes.LOAD_CHARGING_SESSION_STATE: {
            const { sessionState, requestedAt } = action.payload || {};
            if (!sessionState || !sessionState.transactionId) return state;

            const existing = state.chargingSessionStates?.[sessionState.transactionId];
            if (existing && Number.isFinite(existing.receivedAt) && existing.receivedAt > requestedAt) return state;

            return {
                ...state,
                chargingSessionStates: {
                    ...state.chargingSessionStates,
                    [sessionState.transactionId]: { ...sessionState, receivedAt: Date.now() },
                },
            };
        }

        case chargerActionTypes.CLEAR_CHARGING_SESSION_STATE: {
            const transactionId = action.payload;
            if (!transactionId || !state.chargingSessionStates) return state;

            const remaining = { ...state.chargingSessionStates };
            delete remaining[transactionId];

            return { ...state, chargingSessionStates: remaining };
        }

        case chargerActionTypes.UPDATE_CHARGER_STATUS:
            return {
                ...state,
                chargers: {
                    ...state.chargers,
                    chargersArray: updateChargerStatus(state.chargers.chargersArray, action.payload, 'id'),
                    driverChargersArray: updateChargerStatus(state.chargers.driverChargersArray, action.payload, 'id'),
                    publicChargersArray: updateChargerStatus(state.chargers.publicChargersArray, action.payload, 'id'),
                }
            };
        case chargerActionTypes.CLEAR_TRANSACTION_DATA:
            return {
                ...state,
                chargers: {
                    ...state.chargers,
                    chargersArray: state.chargers.chargersArray.map(({ transaction: _, ...rest }) => rest),
                    driverChargersArray: state.chargers.driverChargersArray.map(({ transaction: _, ...rest }) => rest),
                    publicChargersArray: state.chargers.publicChargersArray.map(({ transaction: _, ...rest }) => rest),
                }
            };
        /*
         * spec 000209 (A4) — a SignalR frame no longer writes the GLOBAL loading flag.
         *
         * This case used to set `stateInfo.isLoading` from `isLoadingWhenConnectorStatus`: true whenever a frame
         * reported `Preparing` on a charge point with auto-charging on. `App.js` ORs that flag with five other
         * reducers' and renders `classes.loader` from it — a fixed, full-screen, non-dismissible overlay
         * (position: fixed; inset: 0; z-index: 3000).
         *
         * So somebody plugging in a cable froze the app for every user who could see that charge point, whatever
         * screen they were on and whatever they were typing. Worse, it was only ever cleared by the NEXT frame, and
         * EVMain drops unchanged statuses — a connector left sitting in `Preparing` sends no further frame, so the
         * overlay could stay up indefinitely. A status broadcast is information; it is not a reason to take the
         * application away from the person using it.
         *
         * `stateInfo` is otherwise untouched (clarification Q6 = B): every real request still sets it, and the
         * loader still covers app bootstrap and genuine fetches.
         *
         * Nothing else read this flag, so nothing replaces it here. If a cue for "your charger is auto-starting"
         * is wanted, it belongs on the screen that shows that charger, as a non-blocking indicator — a separate,
         * deliberate piece of UI, not a side effect of a broadcast.
         */
        case chargerActionTypes.UPDATE_CONNECTOR_STATUS:
            return {
                ...state,
                chargers: {
                    ...state.chargers,
                    chargersArray: updateConnectorStatus(state.chargers.chargersArray, action.payload, 'id'),
                    driverChargersArray: updateConnectorStatus(state.chargers.driverChargersArray, action.payload, 'id'),
                    publicChargersArray: updateConnectorStatus(state.chargers.publicChargersArray, action.payload, 'id'),
                },
            };
        case chargerActionTypes.UPDATE_CHARGING_SUMMERY:
            return {
                ...state,
                chargingSummery: action.payload,
            };

        case chargerActionTypes.RESET_STATE_INFO:
            return {
                ...state,
                stateInfo: initialState.stateInfo
            };
        case connectorActionTypes.GET_TRANSACTION_SUMMERY_SUCCESS:
            return {
                ...state,
                chargers: {
                    ...state.chargers,
                    chargersArray: getTransactionDataSummery(state.chargers.chargersArray, action.payload),
                    driverChargersArray: getTransactionDataSummery(state.chargers.driverChargersArray, action.payload),
                    publicChargersArray: getTransactionDataSummery(state.chargers.publicChargersArray, action.payload),
                }
            };
        case "LOGOUT":
            return initialState;
        default:
            return state;
    }
};


const updateChargerStatus = (arr, payload, field) => {
    return arr.map((item) => {
        if (item[field] === payload[field]) {
            return {
                ...item,
                status: payload.status,
            };
        } else {
            return item;
        }
    });
}
//OLD
const updateTransactionData = (arr, payload) => {
    return arr.map((item) => {
        if (item.chargerId === payload.chargePointId) {
            return {
                ...item,
                connectors: item.connectors.map((connector, index) => {
                    if (connector.connectorNumber === payload.connectorId) {
                        // spec 000206 — a frame for a DIFFERENT session than the one stored starts from a clean
                        // transaction. The store can still hold the previous session (the connector pointer
                        // outlives a stop — spec 000201), and spreading it carried the old driver, smart flag and
                        // graph points into the new charge.
                        const isOtherSession = Boolean(payload.localTransactionId)
                            && Boolean(connector?.transaction?.transactionId)
                            && connector.transaction.transactionId !== payload.localTransactionId;
                        const existingTransaction = isOtherSession ? undefined : connector?.transaction;
                        const newTransactionDataSummary = {
                            chargingStrategyTotalDataList: payload.transactionDataSummary?.chargingStrategyTotalDataList ? payload.transactionDataSummary?.chargingStrategyTotalDataList : existingTransaction?.transactionDataSummary?.chargingStrategyTotalDataList,
                            // Preserve existing chargingDataList or create a new array with current payload data
                            chargingDataList: existingTransaction?.transactionDataSummary?.chargingDataList
                                ? [...existingTransaction.transactionDataSummary.chargingDataList, payload.data]
                                : existingTransaction?.transactionDataSummary?.chargingDataList,
                        };
                        // spec 000205 / 000206 — the targeted frame carries the session fields the Charging status
                        // frame delivered before spec 000160 stripped it: who is charging (name, phone, user id —
                        // "my active charge" detection reads the id), the initiator type (guest label), the
                        // transaction id and the smart-charging flag. Each is taken only when the frame brings
                        // it, so an older EVMain that omits them leaves what the list loaded untouched.
                        const transactionId = payload.localTransactionId || existingTransaction?.transactionId;
                        return {
                            ...connector,
                            transactionId: payload.localTransactionId || connector.transactionId,
                            transaction: {
                                ...existingTransaction,
                                ...(transactionId ? { transactionId } : {}),
                                ...(payload.initiatorDetails ? { initiator: payload.initiatorDetails } : {}),
                                ...(payload.transactionIntiatorType ? { transactionIntiatorType: payload.transactionIntiatorType } : {}),
                                ...(typeof payload.isManagedTransaction === 'boolean' ? { isManagedTransaction: payload.isManagedTransaction } : {}),
                                energy: payload.data.energy,
                                total_amp: payload.data.total_amp,
                                price: payload.price,
                                // spec 000196 Round 7 (B4) — when the price arrived; the estimate adds live parking growth only after it.
                                priceReceivedAt: Date.now(),
                                soc: payload.data.soc,
                                startChargeTime: payload?.startChargeTime,
                                transactionDataSummary: newTransactionDataSummary,
                                power: payload.data.power,
                                voltage: payload.data.voltage,
                                maxVoltage: payload.data.MaxVoltage ?? payload.data.maxVoltage
                            }
                        };
                    }
                    return connector
                }),
            };
        } else {
            return item;
        }
    });
}

const getTransactionDataSummery = (arr, payload) => {
    return arr.map((item) => {
        if (item.chargerId === payload.chargerId) {
            return {
                ...item,
                connectors: item.connectors.map((connector, index) => {
                    if (connector.connectorNumber === payload.connectorId) {
                        return {
                            ...connector,
                            // chargingStrategyTotalDataList: payload.chargingStrategyTotalDataList,
                            transaction: {
                                ...connector.transaction,
                                transactionDataSummary: {
                                    ...connector.transaction?.transactionDataSummary,
                                    chargingStrategyTotalDataList: payload.transactionDataSummery.chargingStrategyTotalDataList,
                                    chargingDataList: [
                                        ...payload.transactionDataSummery.chargingDataList
                                    ],
                                }


                            }
                        };
                    }
                    return connector
                }),
            };
        } else {
            return item;
        }
    });
}

const updateConnectorStatus = (arr, payload, field) => {
    return arr.map((item) => {
        if (item.id === payload.chargePointNO) {
            return {
                ...item,
                connectors: item.connectors.map((connector) => {
                    if (connector[field] === payload[field]) {
                        const payloadHasTransaction = payload.transaction !== undefined && payload.transaction !== null;
                        // spec 000206 — the server says the connector holds no transaction. EVMain publishes the
                        // pointer as it is after the Preparing cleanup (ClearStuckTransactionOnConnector), so
                        // transactionId <= 0 is a fact, not a guess. This is what the pre-000160 frame told us by
                        // carrying a null transaction; without it the finished session stayed in the store and
                        // connectorReadyForStartCharging blocked the next start ("connector is free but cannot
                        // start") until a refresh. A frame without a number (older EVMain) changes nothing.
                        const serverSaysNoTransaction = !payloadHasTransaction
                            && typeof payload.transactionId === 'number'
                            && payload.transactionId <= 0;
                        return {
                            ...connector,
                            status: payload.status,
                            ...(serverSaysNoTransaction ? { transactionId: 0 } : {}),
                            // Only overwrite transaction when the payload explicitly carries a value.
                            // An absent field (undefined) means the server didn't send transaction
                            // data this tick — preserve whatever the connector already has.
                            //
                            // spec 000160 — null counts as "didn't send", not as "clear it". Since EVMain
                            // started stripping driver identity from the broadcast, a connector-status
                            // frame carries transaction as an explicit null rather than omitting the
                            // field: ToAvailabilityPayload leaves the property unset, JsonConvert writes
                            // it as null, and SignalR's default protocol serialises nulls. Checking only
                            // for undefined therefore WIPED a live transaction mid-charge, and the
                            // initiator's name never came back — TransDataNotif spreads the existing
                            // transaction and carries no initiator of its own.
                            //
                            // spec 000206 correction: something DID go stale — connectorReadyForStartCharging reads
                            // the transaction on an idle connector. It is now cleared when the server says there is
                            // none (above); mid-charge frames carry a positive id and still preserve it.
                            transaction: payloadHasTransaction
                                ? payload.transaction
                                : (serverSaysNoTransaction ? undefined : connector.transaction),
                        };
                    }
                    return connector;
                }),
            };
        }
        return item;
    });
};

// spec 000209 (A4) — `isLoadingWhenConnectorStatus` was deleted with its only call site above. It answered
// "is one of this user's auto-charging chargers preparing", and that answer was written into the GLOBAL
// loading flag, which App.js renders as a blocking full-screen overlay.



export default chargerReducer;

